import type { ManualPlacementConfig, PlacementConfig, PublisherPlatform } from '../types/metaAdDraft.js';

// Stage 7A D3.1 — the full Meta placement matrix. Single source of truth for the
// compose UI, the publish mapper (Feed/Story asset routing), validation, and the
// confirmation summary. Pure data + helpers (no React, no I/O).
//
// Asset class:
//   'feed'  → uses the Feed image (1:1 / 1.91:1 / 4:5), the body's defaultImageUrl.
//   'story' → uses the Story image (9:16), pinned via a placementAssets rule.
//
// SDK-enum caveat: a few values below (facebook biz_disco_feed; audience_network
// native + instream_video) are NOT in @zernio/node@0.2.203's create-body position
// enums. They store as string[] and ride the loose publish body — operator-verified.

export type PlacementPlatform = PublisherPlatform; // 'facebook' | 'instagram' | 'messenger' | 'audience_network'
export type AssetClass = 'feed' | 'story';

export type PlacementPosition = {
  value: string; // Meta position id (within its platform)
  platform: PlacementPlatform;
  label: string; // human-readable
  assetClass: AssetClass;
  defaultOn?: boolean; // part of the "Common set" (the default Manual selection)
};

export const PLATFORMS: Array<{ value: PlacementPlatform; label: string; defaultExpanded: boolean; note?: string }> = [
  { value: 'facebook', label: 'Facebook', defaultExpanded: true },
  { value: 'instagram', label: 'Instagram', defaultExpanded: true },
  { value: 'messenger', label: 'Messenger', defaultExpanded: false },
  {
    value: 'audience_network',
    label: 'Audience Network',
    defaultExpanded: false,
    note: '(less common for franchise marketing)',
  },
];

export const PLACEMENT_MATRIX: PlacementPosition[] = [
  // Facebook
  { value: 'feed', platform: 'facebook', label: 'Facebook Feed', assetClass: 'feed', defaultOn: true },
  { value: 'marketplace', platform: 'facebook', label: 'Marketplace', assetClass: 'feed' },
  { value: 'video_feeds', platform: 'facebook', label: 'Video Feeds', assetClass: 'feed' },
  { value: 'profile_feed', platform: 'facebook', label: 'Profile Feed', assetClass: 'feed' },
  { value: 'biz_disco_feed', platform: 'facebook', label: 'Business Discovery Feed', assetClass: 'feed' },
  { value: 'notification', platform: 'facebook', label: 'Notifications', assetClass: 'feed' },
  { value: 'search', platform: 'facebook', label: 'Search Results', assetClass: 'feed' },
  { value: 'right_hand_column', platform: 'facebook', label: 'Right Column', assetClass: 'feed' },
  { value: 'instream_video', platform: 'facebook', label: 'In-stream Video', assetClass: 'feed' },
  { value: 'story', platform: 'facebook', label: 'Facebook Story', assetClass: 'story', defaultOn: true },
  { value: 'facebook_reels', platform: 'facebook', label: 'Facebook Reels', assetClass: 'story', defaultOn: true },
  { value: 'facebook_reels_overlay', platform: 'facebook', label: 'Reels Overlay', assetClass: 'feed' },
  // Instagram
  { value: 'stream', platform: 'instagram', label: 'Instagram Feed', assetClass: 'feed', defaultOn: true },
  { value: 'explore', platform: 'instagram', label: 'Explore', assetClass: 'feed' },
  { value: 'explore_home', platform: 'instagram', label: 'Explore Home', assetClass: 'feed' },
  { value: 'profile_feed', platform: 'instagram', label: 'Profile Feed', assetClass: 'feed' },
  { value: 'ig_search', platform: 'instagram', label: 'Instagram Search', assetClass: 'feed' },
  { value: 'story', platform: 'instagram', label: 'Instagram Story', assetClass: 'story', defaultOn: true },
  { value: 'reels', platform: 'instagram', label: 'Instagram Reels', assetClass: 'story', defaultOn: true },
  // Messenger
  { value: 'messenger_home', platform: 'messenger', label: 'Messenger Inbox', assetClass: 'feed' },
  { value: 'story', platform: 'messenger', label: 'Messenger Story', assetClass: 'story' },
  // Audience Network
  { value: 'classic', platform: 'audience_network', label: 'Native, Banner & Interstitial', assetClass: 'feed' },
  { value: 'rewarded_video', platform: 'audience_network', label: 'Rewarded Video', assetClass: 'story' },
  { value: 'native', platform: 'audience_network', label: 'Native', assetClass: 'feed' },
  { value: 'instream_video', platform: 'audience_network', label: 'In-stream Video', assetClass: 'feed' },
];

// Known Meta co-selection dependencies — keyed "<platform>:<position>" → required
// "<platform>:<position>"s. Document new pairs here. (Auto-pair logic in placements.ts.)
export const PLACEMENT_DEPENDENCIES: Record<string, string[]> = {
  'instagram:explore_home': ['instagram:explore'],
};

// The per-platform position array key on ManualPlacementConfig.
const PLATFORM_KEY: Record<PlacementPlatform, keyof Omit<ManualPlacementConfig, 'publisherPlatforms'>> = {
  facebook: 'facebookPositions',
  instagram: 'instagramPositions',
  messenger: 'messengerPositions',
  audience_network: 'audienceNetworkPositions',
};

export function positionArrayKey(platform: PlacementPlatform) {
  return PLATFORM_KEY[platform];
}

export function positionsForPlatform(platform: PlacementPlatform): PlacementPosition[] {
  return PLACEMENT_MATRIX.filter((p) => p.platform === platform);
}

export function findPosition(platform: PlacementPlatform, value: string): PlacementPosition | undefined {
  return PLACEMENT_MATRIX.find((p) => p.platform === platform && p.value === value);
}

// Is this enabled position a Story-class one (needs the 9:16 Story image)?
export function isStoryPosition(platform: PlacementPlatform, value: string): boolean {
  return findPosition(platform, value)?.assetClass === 'story';
}

// The Story-class position VALUES enabled on a config, per platform (mapper uses
// these to build the placementAssets rule).
export function enabledStoryPositions(cfg: ManualPlacementConfig): Record<PlacementPlatform, string[]> {
  const out = {} as Record<PlacementPlatform, string[]>;
  for (const platform of Object.keys(PLATFORM_KEY) as PlacementPlatform[]) {
    out[platform] = (cfg[PLATFORM_KEY[platform]] as string[]).filter((v) => isStoryPosition(platform, v));
  }
  return out;
}

// True when ANY enabled position (any platform) is Story-class.
export function hasAnyStoryPosition(cfg: ManualPlacementConfig): boolean {
  const s = enabledStoryPositions(cfg);
  return (Object.values(s) as string[][]).some((arr) => arr.length > 0);
}

// The default "Common set" applied when an AM first switches to Manual mode:
// FB Feed/Story/Reels + IG Feed/Story/Reels (the defaultOn positions).
export function defaultCommonConfig(): ManualPlacementConfig {
  const cfg: ManualPlacementConfig = {
    publisherPlatforms: ['facebook', 'instagram'],
    facebookPositions: [],
    instagramPositions: [],
    messengerPositions: [],
    audienceNetworkPositions: [],
  };
  for (const pos of PLACEMENT_MATRIX) {
    if (pos.defaultOn) (cfg[PLATFORM_KEY[pos.platform]] as string[]).push(pos.value);
  }
  return cfg;
}

// D3.1 migration — legacy manualConfig predates messengerPositions /
// audienceNetworkPositions (and the widened publisherPlatforms). Default the
// missing arrays to [] so existing drafts keep their selections. Idempotent.
function arr(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}
export function normalizeManualPlacement(cfg: unknown): ManualPlacementConfig {
  const c = (cfg ?? {}) as Record<string, unknown>;
  return {
    publisherPlatforms: arr(c.publisherPlatforms) as PublisherPlatform[],
    facebookPositions: arr(c.facebookPositions),
    instagramPositions: arr(c.instagramPositions),
    messengerPositions: arr(c.messengerPositions),
    audienceNetworkPositions: arr(c.audienceNetworkPositions),
  };
}
export function migratePlacements(placements: unknown): PlacementConfig {
  const p = (placements ?? {}) as Record<string, unknown>;
  if (p.mode === 'manual') {
    return { mode: 'manual', manualConfig: normalizeManualPlacement(p.manualConfig) };
  }
  return { mode: 'automatic', manualConfig: null };
}

// Remove every Story-class position from each platform array (used at publish when
// there's no Story image — Story placements can't deliver without a 9:16 asset).
export function stripStoryPositions(cfg: ManualPlacementConfig): ManualPlacementConfig {
  const out: ManualPlacementConfig = { ...cfg };
  for (const platform of Object.keys(PLATFORM_KEY) as PlacementPlatform[]) {
    const key = PLATFORM_KEY[platform];
    out[key] = (cfg[key] as string[]).filter((v) => !isStoryPosition(platform, v));
  }
  out.publisherPlatforms = (Object.keys(PLATFORM_KEY) as PlacementPlatform[]).filter(
    (p) => (out[PLATFORM_KEY[p]] as string[]).length > 0,
  );
  return out;
}

// The body.placements object from a manual config — only the non-empty arrays +
// publisherPlatforms. null when nothing is selected.
export function manualPlacementsBody(cfg: ManualPlacementConfig): Record<string, unknown> | null {
  const body: Record<string, unknown> = {};
  if (cfg.facebookPositions.length) body.facebookPositions = [...cfg.facebookPositions];
  if (cfg.instagramPositions.length) body.instagramPositions = [...cfg.instagramPositions];
  if (cfg.messengerPositions.length) body.messengerPositions = [...cfg.messengerPositions];
  if (cfg.audienceNetworkPositions.length) body.audienceNetworkPositions = [...cfg.audienceNetworkPositions];
  if (Object.keys(body).length === 0) return null;
  body.publisherPlatforms = (Object.keys(PLATFORM_KEY) as PlacementPlatform[]).filter(
    (p) => (cfg[PLATFORM_KEY[p]] as string[]).length > 0,
  );
  return body;
}

// Meta-NATIVE (snake_case) placement keys for a PlacementConfig, for injection into
// a `rawTargeting` (extracted) publish body. On the rawTargeting path Zernio forwards
// the spec VERBATIM to Meta and bypasses the camelCase placement builder, so the
// top-level `placements` wrapper is silently ignored — placements MUST ride inside
// rawTargeting as Meta-native keys (publisher_platforms / facebook_positions /
// instagram_positions / …). Automatic mode (or no selection) → {} (no keys → Meta
// auto), matching the camelCase path which sends no placements for Automatic. Manual
// → the SAME positions as manualPlacementsBody, in snake_case. Pure.
const CAMEL_TO_SNAKE_PLACEMENT: Record<string, string> = {
  publisherPlatforms: 'publisher_platforms',
  facebookPositions: 'facebook_positions',
  instagramPositions: 'instagram_positions',
  messengerPositions: 'messenger_positions',
  audienceNetworkPositions: 'audience_network_positions',
};
export function rawTargetingPlacements(placements: PlacementConfig): Record<string, unknown> {
  if (placements.mode !== 'manual' || !placements.manualConfig) return {};
  const camel = manualPlacementsBody(placements.manualConfig);
  if (!camel) return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(camel)) {
    const snake = CAMEL_TO_SNAKE_PLACEMENT[k];
    if (snake) out[snake] = v;
  }
  return out;
}

// The placementAssets rule's `placements` from the enabled Story positions per
// platform (only platforms/arrays that actually have a Story position).
export function storyRulePlacements(cfg: ManualPlacementConfig): Record<string, unknown> {
  const s = enabledStoryPositions(cfg);
  const placements: Record<string, unknown> = {};
  const platforms: PlacementPlatform[] = [];
  if (s.facebook.length) { placements.facebookPositions = s.facebook; platforms.push('facebook'); }
  if (s.instagram.length) { placements.instagramPositions = s.instagram; platforms.push('instagram'); }
  if (s.messenger.length) { placements.messengerPositions = s.messenger; platforms.push('messenger'); }
  if (s.audience_network.length) { placements.audienceNetworkPositions = s.audience_network; platforms.push('audience_network'); }
  placements.publisherPlatforms = platforms;
  return placements;
}

// Count enabled positions by asset class across all platforms (confirmation summary).
export function placementCounts(cfg: ManualPlacementConfig): { total: number; feed: number; story: number } {
  let feed = 0;
  let story = 0;
  for (const platform of Object.keys(PLATFORM_KEY) as PlacementPlatform[]) {
    for (const v of cfg[PLATFORM_KEY[platform]] as string[]) {
      if (isStoryPosition(platform, v)) story += 1;
      else feed += 1;
    }
  }
  return { total: feed + story, feed, story };
}
